import React, { useState, useEffect } from "react";
import { WEBCHAT_CONFIG, CHAT_API_BASE } from "./webchatConfig";

const ChatHistory = ({
  isOpen,
  onClose,
  isDarkMode,
  onSelectChat,
  currentConversationId,
  onDeleteBotpressMessages,
  refreshKey,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [pinnedChats, setPinnedChats] = useState([]);
  const [sortBy, setSortBy] = useState("recent"); // recent, oldest, alphabetical
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [chatToDelete, setChatToDelete] = useState(null);

  console.log("🔷 ChatHistory Component Rendered");
  console.log("📂 isOpen:", isOpen);
  console.log("🎨 isDarkMode:", isDarkMode);
  console.log("📌 currentConversationId:", currentConversationId);

  // Load chat history from localStorage and API
  useEffect(() => {
    if (isOpen) {
      console.log("🔄 useEffect triggered - Loading chat history...");
      loadChatHistory();
      loadPinnedChats();
    }
  }, [isOpen, refreshKey]);

  // Load chat history from both localStorage and API
  const loadChatHistory = async () => {
    console.log("📥 loadChatHistory() called");
    setIsLoading(true);

    try {
      // First, load from localStorage (fast, immediate)
      const historyKey = `chatHistory_${WEBCHAT_CONFIG.clientName}`;
      const storedHistory = localStorage.getItem(historyKey);

      if (storedHistory) {
        const localHistory = JSON.parse(storedHistory);
        console.log("✅ Loaded", localHistory.length, "chats from localStorage");
        setChatHistory(localHistory);
      }

      // Then, try to load from API (slower, but more comprehensive)
      const storedSession = localStorage.getItem(WEBCHAT_CONFIG.clientName);
      if (storedSession) {
        const session = JSON.parse(storedSession);
        const userId = session.userId;
        const companyUuid = WEBCHAT_CONFIG.uuid;

        if (userId) {
          console.log("🌐 Fetching conversations from API...");
          console.log("👤 User ID:", userId);

          const response = await fetch(
            `${CHAT_API_BASE}/conversation/get-conversation-history/${userId}/${companyUuid}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
              },
            }
          );

          if (response.ok) {
            const result = await response.json();
            console.log("✅ API Response:", result);

            let conversations = [];
            if (result.data && Array.isArray(result.data)) {
              conversations = result.data;
            } else if (result.success && result.data && result.data.conversations) {
              conversations = result.data.conversations;
            }

            if (conversations) {
              // The API returns every conversation, including ones marked deleted.
              // Drop deleted entries so they don't show up and don't affect sync.
              conversations = conversations.filter(
                (conv) => conv.status !== "deleted"
              );

              console.log("📊 API returned", conversations.length, "non-deleted conversations");

              // Merge API data with localStorage data
              const localHistory = storedHistory ? JSON.parse(storedHistory) : [];
              const mergedHistory = mergeConversations(localHistory, conversations, userId);

              // Sync: remove CONVO_ chats that the API no longer returns (e.g. deleted)
              // Keep local-only chats (no CONVO_ key) and pinned chats.
              let storedPinned = [];
              try {
                storedPinned = JSON.parse(localStorage.getItem("pinnedChats") || "[]");
              } catch (e) {
                storedPinned = [];
              }
              const apiSidebarKeys = new Set(
                conversations
                  .map(getSidebarConversationKey)
                  .filter(Boolean)
              );
              const apiConvoIds = new Set(
                conversations
                  .map(getConvoKeyForDelete)
                  .filter(k => k && String(k).startsWith("CONVO_"))
              );
              const syncedHistory = mergedHistory.filter(chat => {
                const key = chat.conversationKey || chat.id;
                const isPinned = storedPinned.includes(key);

                // Remove API-backed CONVO_ chats that the API no longer returns.
                if (String(key).startsWith("CONVO_") && !apiSidebarKeys.has(key) && !isPinned) {
                  console.log("🗑️ Removing stale/deleted conversation from history:", key);
                  return false;
                }

                // Also remove chats tracked by a separate CONVO_ id if that id is gone.
                if (chat.convoId && String(chat.convoId).startsWith("CONVO_") && !apiConvoIds.has(chat.convoId) && !isPinned) {
                  console.log("🗑️ Removing stale/deleted conversation by convoId:", chat.convoId);
                  return false;
                }

                return true;
              });

              setChatHistory(syncedHistory);

              // Update localStorage with merged + synced data
              localStorage.setItem(historyKey, JSON.stringify(syncedHistory));
              console.log("💾 Updated localStorage with synced history");
            }
          } else {
            console.warn("⚠️ API returned status:", response.status);
          }
        }
      }
    } catch (error) {
      console.error("❌ Error loading chat history:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Key used for the sidebar list and message loading.
  // Always prefer a CONVO_ id over any other id (e.g. MongoDB _id or conv_ id).
  const getSidebarConversationKey = (apiConv) => {
    const candidates = [
      apiConv.conversationKey,
      apiConv.conversationId,
      apiConv.vcxConversationId,
      apiConv.vcx_conversation_id,
      apiConv.vcx_id,
      apiConv.id,
    ].filter(Boolean);

    const convoKey = candidates.find(k => String(k).startsWith("CONVO_"));
    return convoKey || candidates[0] || null;
  };

  // Key used for delete API — prefer the public CONVO_ id the backend uses.
  const getConvoKeyForDelete = (apiConv) => {
    const candidates = [
      apiConv.conversationKey,
      apiConv.conversationId,
      apiConv.vcxConversationId,
      apiConv.vcx_conversation_id,
      apiConv.vcx_id,
      apiConv.id,
    ].filter(Boolean);

    const convoKey = candidates.find(k => String(k).startsWith("CONVO_"));
    return convoKey || candidates[0] || null;
  };

  // Tokenize a title for fuzzy comparison.
  const getTitleTokens = (title) => {
    return (title || "")
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(t => t.length > 1);
  };

  const tokenMatchRatio = (title1, title2) => {
    const tokens1 = new Set(getTitleTokens(title1));
    const tokens2 = new Set(getTitleTokens(title2));
    if (tokens1.size === 0 || tokens2.size === 0) return 0;
    const intersection = [...tokens1].filter(t => tokens2.has(t));
    const union = new Set([...tokens1, ...tokens2]);
    return intersection.length / union.size;
  };

  // Merge conversations from localStorage and API.
  // API is the source of truth. localStorage entries that match an API entry
  // by key or by fuzzy title/time are treated as duplicates and dropped.
  // API entries with null/"null" conversationId are also dropped.
  const mergeConversations = (localHistory, apiConversations, userId) => {
    const ONE_DAY = 24 * 60 * 60 * 1000;
    const GENERIC_TITLES = new Set(["Your Conversation", "Conversation", "New Conversation", ""]);

    // Build the API-backed list, skipping null/placeholder conversations.
    const apiEntries = apiConversations
      .map(apiConv => {
        const convKey = getSidebarConversationKey(apiConv);
        if (!convKey || convKey === "null") return null;
        const convoDeleteKey = getConvoKeyForDelete(apiConv);

        const apiTitle = (apiConv.title || apiConv.conversationName || apiConv.lastMessage?.substring(0, 50) || "Conversation").trim();

        // Prefer the locally stored title over the API's generic title
        const localEntry = localHistory.find(l => l.conversationKey === convKey);
        const localTitle = localEntry?.title?.trim() || "";
        const resolvedTitle = GENERIC_TITLES.has(apiTitle) && localTitle && !GENERIC_TITLES.has(localTitle)
          ? localTitle
          : apiTitle;
        return {
          id: convKey,
          conversationKey: convKey,
          convoId: convoDeleteKey && convoDeleteKey !== convKey ? convoDeleteKey : undefined,
          title: resolvedTitle,
          lastMessage: apiConv.lastMessage || "",
          timestamp: apiConv.timestamp || apiConv.lastMessageTimestamp || apiConv.createdAt || new Date().toISOString(),
          messageCount: apiConv.messageCount || 0,
          userId: apiConv.userId || apiConv.senderId || userId,
          status: apiConv.status || "inactive",
        };
      })
      .filter(Boolean);

    const apiKeys = new Set(apiEntries.map(e => e.conversationKey));

    // Keep localStorage entries only if they are NOT duplicates of an API entry.
    const uniqueLocalEntries = localHistory.filter(local => {
      if (apiKeys.has(local.conversationKey)) return false;

      const localTs = new Date(local.timestamp || 0).getTime();
      return !apiEntries.some(api => {
        const apiTs = new Date(api.timestamp || 0).getTime();
        const timeMatches = Math.abs(localTs - apiTs) < ONE_DAY;
        const ratio = tokenMatchRatio(local.title, api.title);
        return timeMatches && ratio >= 0.5;
      });
    });

    const merged = [...apiEntries, ...uniqueLocalEntries];
    merged.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    return merged;
  };

  const loadPinnedChats = () => {
    console.log("📌 loadPinnedChats() called");
    try {
      const stored = localStorage.getItem("pinnedChats");
      if (stored) {
        const pinned = JSON.parse(stored);
        console.log("✅ Loaded pinned chats:", pinned);
        setPinnedChats(pinned);
      } else {
        console.log("📌 No pinned chats found");
      }
    } catch (error) {
      console.error("❌ Error loading pinned chats:", error);
    }
  };

  const togglePin = (chatId) => {
    console.log("📌 togglePin() called for:", chatId);
    const newPinnedChats = pinnedChats.includes(chatId)
      ? pinnedChats.filter((id) => id !== chatId)
      : [...pinnedChats, chatId];

    console.log("📌 New pinned chats:", newPinnedChats);
    setPinnedChats(newPinnedChats);
    localStorage.setItem("pinnedChats", JSON.stringify(newPinnedChats));
    console.log("💾 Saved pinned chats to localStorage");
  };

  const deleteChat = async (chatId) => {
    console.log("🗑️ deleteChat() called for:", chatId);

    const chat = chatHistory.find((c) => c.id === chatId);
    const conversationId =
      chat?.convoId ||
      [chat?.conversationKey, chat?.id, chatId].find(k => String(k).startsWith("CONVO_")) ||
      chat?.conversationKey ||
      chat?.id ||
      chatId;
    const companyUuid = WEBCHAT_CONFIG.uuid;

    try {
      console.log("🌐 Calling delete-conversation API...", {
        conversationId,
        companyUuid,
      });
      const response = await fetch(
        `https://chat.versalence.info/whatsapp/v2/conversation/delete-conversation/${conversationId}/${companyUuid}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        console.warn("⚠️ Delete conversation API returned status:", response.status);
        return;
      }

      const result = await response.json();
      console.log("✅ Delete conversation API response:", result);
    } catch (error) {
      console.error("❌ Error calling delete conversation API:", error);
      return;
    }

    // Also hard-delete the underlying Botpress messages for this user.
    if (onDeleteBotpressMessages) {
      onDeleteBotpressMessages();
    }

    const historyKey = `chatHistory_${WEBCHAT_CONFIG.clientName}`;
    const newHistory = chatHistory.filter((chat) => chat.id !== chatId);

    console.log("📊 New history length:", newHistory.length);
    setChatHistory(newHistory);
    localStorage.setItem(historyKey, JSON.stringify(newHistory));

    // Also remove from pinned if it was pinned
    if (pinnedChats.includes(chatId)) {
      console.log("📌 Removing from pinned chats");
      const newPinnedChats = pinnedChats.filter((id) => id !== chatId);
      setPinnedChats(newPinnedChats);
      localStorage.setItem("pinnedChats", JSON.stringify(newPinnedChats));
    }
    console.log("✅ Chat deleted successfully");
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return date.toLocaleDateString();
  };

  const getSortedChats = () => {
    console.log("🔄 getSortedChats() called with sortBy:", sortBy);
    let sorted = [...chatHistory];

    switch (sortBy) {
      case "recent":
        sorted.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        break;
      case "oldest":
        sorted.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        break;
      case "alphabetical":
        sorted.sort((a, b) => a.title.localeCompare(b.title));
        break;
      default:
        break;
    }

    console.log("✅ Sorted chats:", sorted.length, "conversations");
    return sorted;
  };

  const getFilteredChats = () => {
    console.log("🔍 getFilteredChats() called with searchQuery:", searchQuery);
    const sorted = getSortedChats();
    if (!searchQuery.trim()) {
      console.log("✅ No search query, returning all:", sorted.length, "chats");
      return sorted;
    }

    const filtered = sorted.filter(
      (chat) =>
        chat.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        chat.lastMessage.toLowerCase().includes(searchQuery.toLowerCase())
    );

    console.log("✅ Filtered chats:", filtered.length, "matches");
    return filtered;
  };

  const groupChatsByDate = (chats) => {
    const groups = {
      pinned: [],
      today: [],
      yesterday: [],
      thisWeek: [],
      thisMonth: [],
      older: [],
    };

    const now = new Date();
    const todayStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    const yesterdayStart = new Date(todayStart.getTime() - 86400000);
    const weekStart = new Date(todayStart.getTime() - 7 * 86400000);
    const monthStart = new Date(todayStart.getTime() - 30 * 86400000);

    chats.forEach((chat) => {
      if (pinnedChats.includes(chat.id)) {
        groups.pinned.push(chat);
      } else {
        const chatDate = new Date(chat.timestamp);
        if (chatDate >= todayStart) {
          groups.today.push(chat);
        } else if (chatDate >= yesterdayStart) {
          groups.yesterday.push(chat);
        } else if (chatDate >= weekStart) {
          groups.thisWeek.push(chat);
        } else if (chatDate >= monthStart) {
          groups.thisMonth.push(chat);
        } else {
          groups.older.push(chat);
        }
      }
    });

    return groups;
  };

  const filteredChats = getFilteredChats();
  const groupedChats = groupChatsByDate(filteredChats);

  const ChatGroup = ({ title, chats }) => {
    if (chats.length === 0) return null;

    return (
      <div className="mb-4">
        <h3
          className={`text-xs font-semibold uppercase mb-1.5 px-2 ${isDarkMode ? "text-gray-600" : "text-gray-500"
            }`}
        >
          {title}
        </h3>
        <div className="space-y-0.5">
          {chats.map((chat) => (
            <div
              key={chat.id}
              className={`group relative rounded-lg p-2.5 cursor-pointer transition-all ${currentConversationId === chat.conversationKey
                ? isDarkMode
                  ? "bg-cyan-500/10 border-l-2 border-cyan-500"
                  : "bg-cyan-50 border-l-2 border-cyan-500"
                : isDarkMode
                  ? "hover:bg-gray-800"
                  : "hover:bg-gray-100"
                }`}
              onClick={() => {
                console.log("🖱️ Chat clicked:", chat);
                onSelectChat(chat);
              }}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h4
                    className={`font-medium text-xs mb-0.5 truncate ${isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                  >
                    {chat.title}
                  </h4>
                  {chat.timestamp && (
                    <p className={`text-xs mb-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}>
                      {new Date(chat.timestamp).toISOString().split('T')[0]}
                    </p>
                  )}
                  <p
                    className={`text-xs line-clamp-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                  >
                    {chat.lastMessage}
                  </p>
                  <div className="flex items-center gap-1 mt-0.5">
                    <span
                      className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-500"
                        }`}
                    >
                      {formatTimestamp(chat.timestamp)}
                    </span>
                    {chat.messageCount > 0 && (
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode
                          ? "bg-gray-700 text-gray-400"
                          : "bg-gray-200 text-gray-600"
                          }`}
                      >
                        {chat.messageCount} msgs
                      </span>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-1 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePin(chat.id);
                    }}
                    className={`p-1.5 rounded hover:bg-gray-700 ${pinnedChats.includes(chat.id)
                      ? "text-cyan-400"
                      : isDarkMode
                        ? "text-gray-500 hover:text-cyan-400"
                        : "text-gray-400 hover:text-cyan-500"
                      }`}
                    title={
                      pinnedChats.includes(chat.id) ? "Unpin" : "Pin to top"
                    }
                  >
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L11 6.477V16h2a1 1 0 110 2H7a1 1 0 110-2h2V6.477L6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z" />
                    </svg>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setChatToDelete(chat);
                    }}
                    className={`p-1.5 rounded ${isDarkMode
                      ? "text-gray-500 hover:text-red-400 hover:bg-gray-700"
                      : "text-gray-400 hover:text-red-500 hover:bg-gray-100"
                      }`}
                    title="Delete conversation"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  console.log("📊 Grouped chats:", {
    pinned: groupedChats.pinned.length,
    today: groupedChats.today.length,
    yesterday: groupedChats.yesterday.length,
    thisWeek: groupedChats.thisWeek.length,
    thisMonth: groupedChats.thisMonth.length,
    older: groupedChats.older.length,
  });

  if (!isOpen) {
    console.log("❌ ChatHistory: Sidebar NOT open - returning null");
    return null;
  }

  console.log("✅ ChatHistory: Sidebar IS OPEN - rendering component!");

  return (
    <>
      {/* Overlay - only on mobile */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden ${isOpen ? "block" : "hidden"
          }`}
        onClick={onClose}
      />

      {/* Sidebar */}
      <div
        className={`fixed top-0 h-full ${isDarkMode
          ? "bg-gray-950 border-gray-800"
          : "bg-white border-gray-200"
          } border-r shadow-2xl transform transition-all duration-300 ease-in-out ${isOpen ? "w-80" : "w-0"
          } overflow-hidden left-0 lg:left-20 z-50 lg:z-30`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-3 border-b border-gray-800">
            <div className="flex items-center justify-between mb-3">
              <h2
                className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"
                  }`}
              >
                Chat History
              </h2>
              <button
                onClick={() => {
                  console.log("🔴 Close button clicked");
                  onClose();
                }}
                className={`p-2 rounded-lg transition-colors ${isDarkMode
                  ? "text-gray-400 hover:text-white hover:bg-gray-800"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  }`}
                title="Close Chat History"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Search Bar */}
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search conversations..."
                className={`w-full pl-10 pr-4 py-2 rounded-lg border ${isDarkMode
                  ? "bg-gray-900 border-gray-800 text-white placeholder-gray-500"
                  : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
                  } focus:outline-none focus:ring-2 focus:ring-cyan-500`}
              />
              <svg
                className={`absolute left-3 top-2.5 w-5 h-5 ${isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>

            {/* Sort Options */}
            <div className="mt-3 flex items-center justify-between">
              <span
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
              >
                {filteredChats.length} conversations
              </span>
              <div className="relative">
                <button
                  onClick={() => setShowSortMenu(!showSortMenu)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${isDarkMode
                    ? "text-gray-400 hover:text-white hover:bg-gray-800"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                    }`}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"
                    />
                  </svg>
                  Sort
                </button>

                {showSortMenu && (
                  <div
                    className={`absolute right-0 mt-2 w-48 rounded-lg shadow-lg border ${isDarkMode
                      ? "bg-gray-900 border-gray-800"
                      : "bg-white border-gray-200"
                      } z-10`}
                  >
                    <div className="py-1">
                      {[
                        { value: "recent", label: "Most Recent" },
                        { value: "oldest", label: "Oldest First" },
                        { value: "alphabetical", label: "A-Z" },
                      ].map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setSortBy(option.value);
                            setShowSortMenu(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm ${sortBy === option.value
                            ? isDarkMode
                              ? "bg-cyan-500/10 text-cyan-400"
                              : "bg-cyan-50 text-cyan-600"
                            : isDarkMode
                              ? "text-gray-300 hover:bg-gray-800"
                              : "text-gray-700 hover:bg-gray-50"
                            }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Chat List */}
          <div className="flex-1 overflow-y-auto p-3">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mb-3"></div>
                <p
                  className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-600"
                    }`}
                >
                  Loading conversations...
                </p>
              </div>
            ) : filteredChats.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full">
                <div
                  className={`text-6xl mb-3 ${isDarkMode ? "text-gray-800" : "text-gray-300"
                    }`}
                >
                  💬
                </div>
                <p
                  className={`text-center ${isDarkMode ? "text-gray-500" : "text-gray-600"
                    }`}
                >
                  {searchQuery
                    ? "No conversations found"
                    : "No chat history yet"}
                </p>
                <p
                  className={`text-center text-sm mt-2 ${isDarkMode ? "text-gray-600" : "text-gray-500"
                    }`}
                >
                  Start a conversation to see it here
                </p>
              </div>
            ) : (
              <>
                <ChatGroup title="Pinned" chats={groupedChats.pinned} />
                <ChatGroup title="Today" chats={groupedChats.today} />
                <ChatGroup title="Yesterday" chats={groupedChats.yesterday} />
                <ChatGroup
                  title="Previous 7 Days"
                  chats={groupedChats.thisWeek}
                />
                <ChatGroup
                  title="Previous 30 Days"
                  chats={groupedChats.thisMonth}
                />
                <ChatGroup title="Older" chats={groupedChats.older} />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {chatToDelete && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => setChatToDelete(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden ${isDarkMode
              ? "bg-gray-800 text-gray-100 border-gray-700"
              : "bg-white text-gray-900 border-gray-200"
              } border`}
          >
            {/* Header */}
            <div className={`flex items-center justify-between p-4 border-b ${isDarkMode ? "border-gray-700" : "border-gray-100"}`}>
              <h3 className="text-lg font-semibold">Delete Conversation</h3>
              <button
                onClick={() => setChatToDelete(null)}
                className={`p-2 rounded-full transition-colors ${isDarkMode
                  ? "hover:bg-gray-700 text-gray-400 hover:text-white"
                  : "hover:bg-gray-100 text-gray-500 hover:text-gray-900"
                  }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="p-5">
              <p className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                Are you sure you want to delete{" "}
                <span className="font-medium">
                  {chatToDelete.title || "this conversation"}
                </span>
                ? This action cannot be undone.
              </p>
            </div>

            {/* Footer */}
            <div className={`p-4 border-t flex justify-end gap-3 ${isDarkMode ? "border-gray-700" : "border-gray-100"}`}>
              <button
                onClick={() => setChatToDelete(null)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDarkMode
                  ? "text-gray-300 hover:bg-gray-700"
                  : "text-gray-600 hover:bg-gray-100"
                  }`}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  deleteChat(chatToDelete.id);
                  setChatToDelete(null);
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-transform active:scale-95 ${isDarkMode
                  ? "bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/30"
                  : "bg-red-50 text-red-600 hover:bg-red-100 border border-red-200"
                  }`}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatHistory;